import React, { useEffect, useState } from "react";
import Modal from "../../../components/modal";
import axios from "axios";
import Swal from "sweetalert2";
import { API_URL } from "../../../utils/constant";
import { Plus, Trash } from "lucide-react";

const SalesCreditNoteForm = ({
  show,
  onClose,
  heading,
  initialData,
  onSuccess,
}) => {
  const initialFormState = {
    company_id: "",
    customer_id: "",
    gst_type: "",
    invoice_no: "",
    sales_cn_no: "",
    ref_no: "",
    ref_date: "",
    date: "",
    tax_type: "",
    against_bill: "",
    against_bill_date: "",
    payment_terms: "",
    due_date: "",
    payment_link: "",
    return_reason: "",
    remarks: "",
    discount: "",
    discount_rs: "",
    status: true,
  };

  const initialItemState = {
    product_id: "",
    description: "",
    unit: "",
    quantity: "",
    rate: "",
    amount: "",
    discount: "",
    tax_rate: "",
    tax_amount: "",
    total_amount: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const company_id = localStorage.getItem("companyID");

    if (initialData) {
      setFormData({
        ...initialData,
        status: initialData.status !== false,
      });
      setItems(initialData.items || []);
    } else {
      setFormData({
        ...initialFormState,
        company_id,
      });
      setItems([]);
    }
  }, [initialData, show]);

  // Handle form change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle item change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    // Auto-calculate amount, tax_amount, total_amount
    if (field === 'quantity' || field === 'rate' || field === 'discount' || field === 'tax_rate') {
      const qty = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      const discount = parseFloat(updatedItems[index].discount) || 0;
      const taxRate = parseFloat(updatedItems[index].tax_rate) || 0;

      const amount = qty * rate;
      const taxableAmount = amount - discount;
      const taxAmount = (taxableAmount * taxRate) / 100;
      const totalAmount = taxableAmount + taxAmount;

      updatedItems[index].amount = amount.toFixed(2);
      updatedItems[index].tax_amount = taxAmount.toFixed(2);
      updatedItems[index].total_amount = totalAmount.toFixed(2);
    }

    setItems(updatedItems);
  };

  // Add new item row
  const handleAddItem = () => {
    setItems([...items, { ...initialItemState }]);
  };

  // Remove item row
  const handleRemoveItem = (index) => {
    const updatedItems = items.filter((_, i) => i !== index);
    setItems(updatedItems);
  };

  // Calculate totals from items
  const calculateTotals = () => {
    let itemValue = 0;
    let totalTax = 0;
    let grandTotal = 0;

    items.forEach(item => {
      itemValue += parseFloat(item.amount) || 0;
      totalTax += parseFloat(item.tax_amount) || 0;
      grandTotal += parseFloat(item.total_amount) || 0;
    });

    return {
      item_value: itemValue.toFixed(2),
      tax_amount: totalTax.toFixed(2),
      total_amount: grandTotal.toFixed(2),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_id || !formData.date) {
      Swal.fire("Validation Error", "Customer and Date are required fields", "warning");
      return;
    }

    if (items.length === 0) {
      Swal.fire("Validation Error", "At least one item is required", "warning");
      return;
    }

    // Check if all items have required fields
    const invalidItems = items.filter(item => !item.product_id || !item.quantity || !item.rate);
    if (invalidItems.length > 0) {
      Swal.fire("Validation Error", "All items must have Product ID, Quantity, and Rate", "warning");
      return;
    }

    const totals = calculateTotals();

    const payload = {
      ...formData,
      ...totals,
      items: items,
    };

    try {
      setLoading(true);

      if (initialData?.id) {
        await axios.put(
          `${API_URL}/sales-credit-notes/${initialData.id}`,
          payload
        );

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Sales Credit Note has been updated.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", Swal.stopTimer);
            toast.addEventListener("mouseleave", Swal.resumeTimer);
          },
        });
      } else {
        await axios.post(`${API_URL}/sales-credit-notes`, payload);
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Sales Credit Note has been created.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", Swal.stopTimer);
            toast.addEventListener("mouseleave", Swal.resumeTimer);
          },
        });
      }

      onClose();
      onSuccess();
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Something went wrong",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} heading={heading} size="lg">
      <form onSubmit={handleSubmit}>
        <div className="row">
          {/* Customer */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Customer <span className="text-danger">*</span></label>
            <input
              type="number"
              className="form-control"
              name="customer_id"
              value={formData.customer_id}
              onChange={handleChange}
              placeholder="Enter Customer ID"
            />
          </div>

          {/* Date */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Date <span className="text-danger">*</span></label>
            <input
              type="date"
              className="form-control"
              name="date"
              value={formData.date ? formData.date.split('T')[0] : ''}
              onChange={handleChange}
              required
            />
          </div>

          {/* GST Type */}
          <div className="col-md-6 mb-3">
            <label className="form-label">GST Type</label>
            <select
              className="form-select"
              name="gst_type"
              value={formData.gst_type}
              onChange={handleChange}
            >
              <option value="">Select GST Type</option>
              <option value="CGST">CGST</option>
              <option value="IGST">IGST</option>
              <option value="SGST">SGST</option>
            </select>
          </div>

          {/* Tax Type */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Tax Type</label>
            <select
              className="form-select"
              name="tax_type"
              value={formData.tax_type}
              onChange={handleChange}
            >
              <option value="">Select Tax Type</option>
              <option value="Inclusive">Inclusive</option>
              <option value="Exclusive">Exclusive</option>
            </select>
          </div>

          {/* Invoice No */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Invoice No</label>
            <input
              type="text"
              className="form-control"
              name="invoice_no"
              value={formData.invoice_no}
              onChange={handleChange}
            />
          </div>

          {/* Sales CN No */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Sales CN No</label>
            <input
              type="text"
              className="form-control"
              name="sales_cn_no"
              value={formData.sales_cn_no}
              onChange={handleChange}
            />
          </div>

          {/* Reference No */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Reference No</label>
            <input
              type="text"
              className="form-control"
              name="ref_no"
              value={formData.ref_no}
              onChange={handleChange}
            />
          </div>

          {/* Reference Date */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Reference Date</label>
            <input
              type="date"
              className="form-control"
              name="ref_date"
              value={formData.ref_date ? formData.ref_date.split('T')[0] : ''}
              onChange={handleChange}
            />
          </div>

          {/* Against Bill */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Against Bill</label>
            <input
              type="number"
              className="form-control"
              name="against_bill"
              value={formData.against_bill}
              onChange={handleChange}
              placeholder="Enter Bill ID"
            />
          </div>

          {/* Against Bill Date */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Against Bill Date</label>
            <input
              type="date"
              className="form-control"
              name="against_bill_date"
              value={formData.against_bill_date ? formData.against_bill_date.split('T')[0] : ''}
              onChange={handleChange}
            />
          </div>

          {/* Payment Terms */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Payment Terms</label>
            <select
              className="form-select"
              name="payment_terms"
              value={formData.payment_terms}
              onChange={handleChange}
            >
              <option value="">Select Payment Terms</option>
              <option value="Immediate">Immediate</option>
              <option value="Net 15">Net 15</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 45">Net 45</option>
              <option value="Net 60">Net 60</option>
            </select>
          </div>

          {/* Due Date */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Due Date</label>
            <input
              type="date"
              className="form-control"
              name="due_date"
              value={formData.due_date ? formData.due_date.split('T')[0] : ''}
              onChange={handleChange}
            />
          </div>

          {/* Payment Link */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Payment Link</label>
            <input
              type="text"
              className="form-control"
              name="payment_link"
              value={formData.payment_link}
              onChange={handleChange}
              placeholder="Enter Payment Link"
            />
          </div>

          {/* Return Reason */}
          <div className="col-md-6 mb-3">
            <label className="form-label">Return Reason</label>
            <input
              type="text"
              className="form-control"
              name="return_reason"
              value={formData.return_reason}
              onChange={handleChange}
              placeholder="Enter Return Reason"
            />
          </div>

          {/* Items Section */}
          <div className="col-12 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">Items</h5>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleAddItem}
              >
                <Plus size={16} className="me-1" /> Add Item
              </button>
            </div>

            {items.length === 0 && (
              <div className="alert alert-info">No items added. Click "Add Item" to add products.</div>
            )}

            {items.map((item, index) => (
              <div key={index} className="border rounded p-3 mb-2 bg-light">
                <div className="row g-2">
                  <div className="col-md-2">
                    <label className="form-label small">Product ID</label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={item.product_id}
                      onChange={(e) => handleItemChange(index, 'product_id', e.target.value)}
                      placeholder="Product ID"
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small">Description</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      placeholder="Description"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small">Unit</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={item.unit}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      placeholder="Unit"
                    />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label small">Qty</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                      placeholder="Qty"
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label small">Rate</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={item.rate}
                      onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                      placeholder="Rate"
                    />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label small">Disc</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={item.discount}
                      onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                      placeholder="Disc"
                    />
                  </div>
                  <div className="col-md-1">
                    <label className="form-label small">Tax %</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={item.tax_rate}
                      onChange={(e) => handleItemChange(index, 'tax_rate', e.target.value)}
                      placeholder="Tax %"
                    />
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm w-100"
                      onClick={() => handleRemoveItem(index)}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-md-4">
                    <small className="text-muted">Amount: <strong>₹{item.amount || '0.00'}</strong></small>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">Tax: <strong>₹{item.tax_amount || '0.00'}</strong></small>
                  </div>
                  <div className="col-md-4">
                    <small className="text-muted">Total: <strong>₹{item.total_amount || '0.00'}</strong></small>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals Summary */}
          <div className="col-12 mb-3">
            <div className="card bg-light">
              <div className="card-body">
                <div className="row">
                  <div className="col-md-3">
                    <label className="form-label">Item Value</label>
                    <input
                      type="text"
                      className="form-control"
                      value={calculateTotals().item_value}
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tax Amount</label>
                    <input
                      type="text"
                      className="form-control"
                      value={calculateTotals().tax_amount}
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Total Amount</label>
                    <input
                      type="text"
                      className="form-control fw-bold"
                      value={calculateTotals().total_amount}
                      readOnly
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Discount (Rs)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      name="discount_rs"
                      value={formData.discount_rs}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className="col-12 mb-3">
            <label className="form-label">Remarks</label>
            <textarea
              className="form-control"
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows="3"
            />
          </div>

          {/* Status */}
          <div className="col-md-6 mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                id="status"
                checked={formData.status}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="status">
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="btn btn-light me-2"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SalesCreditNoteForm;
